import { FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { Box, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Timeline from "../../../GeneralComponents/timeline/Timeline";
import Information from "./parts/information/Information";
import Table from "./parts/table/Table";
import { State } from "../../../../redux/slices";
import {
  fetchRoles,
  fetchGroups,
  fetchLocations,
} from "../../../../redux/actions/users/managerAsyncActionsV2/managerAsyncActionsV2";
import { resetManagerCreation } from "../../../../redux/slices/users/managerCreation";
import { useTranslation } from "react-i18next";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    loadRoles: (token: string) => {
      dispatch(fetchRoles(token));
    },
    loadGroups: (token: string) => {
      dispatch(fetchGroups(token));
    },
    loadLocations: (token: string) => {
      dispatch(fetchLocations(token));
    },
    reset: () => {
      dispatch(resetManagerCreation());
    },
  };
};

const ManagerUsers: FC = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const { loadRoles, loadGroups, reset, loadLocations } = SetActions();
  const { user } = useSelector((state: State) => state.Authentication);
  const { rows, reviewOpen, submitSuccess, submitResults } = useSelector(
    (state: State) => state.ManagerCreationReducer,
  );

  const allSucceeded =
    submitSuccess &&
    submitResults.length > 0 &&
    submitResults.every((r) => r.success);
  const token = user?.token ?? "";

  useEffect(() => {
    loadRoles(token);
    loadGroups(token);
    loadLocations(token);
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = [
    {
      title: t("Users_managers.step_type_title"),
      subtitle: t("Users_managers.step_type_subtitle"),
    },
    {
      title: t("Users_managers.step_config_title"),
      subtitle: t("Users_managers.step_config_subtitle", {
        count: rows.length,
      }),
    },
    {
      title: t("Users_managers.step_review_title"),
      subtitle: allSucceeded
        ? t("Users_managers.step_review_subtitle_success")
        : t("Users_managers.step_review_subtitle"),
    },
  ];

  const activeStep = allSucceeded ? 3 : reviewOpen ? 2 : 1;

  return (
    <div style={{ padding: "20px" }}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button
          variant="text"
          endIcon={<CloseIcon />}
          sx={{ textTransform: "none", color: "#000" }}
          onClick={() => {
            reset();
            navigate("/users");
          }}
        >
          {t("Common_options.Close")}
        </Button>
      </Box>
      <Timeline steps={steps} activeStep={activeStep} />
      <Information />
      <Table />
    </div>
  );
};

export default ManagerUsers;
